import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { AxiosError } from 'axios';
import { FormikConfig, FormikHelpers } from 'formik';
import { createContext, useContext } from 'use-context-selector';
import * as Yup from 'yup';

import { useModal, useResetPasswordMutation } from '~hooks';

export type ResetPasswordValues = {
  password: string;
  confirmPassword: string;
  submit: unknown;
};

export const resetPasswordValidationSchema = Yup.object({
  password: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .matches(/[A-Z]/, 'Must contain at least one uppercase letter')
    .matches(/[a-z]/, 'Must contain at least one lowercase letter')
    .matches(
      /[!@#$%^&*(),.?":{}|<>]/,
      'Must contain at least one special character',
    )
    .matches(/[0-9]/, 'Must contain at least one digit')
    .matches(/^\S*$/, 'Cannot contain spaces')
    .required('Password is required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password'), undefined], 'Passwords must match')
    .required('Confirm password is required'),
});

const initialContextValues = {
  resetPasswordValues: {} as ResetPasswordValues,
  handleSubmit: (
    _: ResetPasswordValues,
    _formikHelpers: FormikHelpers<ResetPasswordValues>,
  ) => {
    return;
  },
  isResettingPassword: false,
  isSuccessPasswordResetDialogOpen: false,
  closeSuccessPasswordResetDialog: () => {
    return;
  },
};

const ResetPasswordContext = createContext(initialContextValues);

type Props = {
  children: React.ReactNode;
};

export const ResetPasswordProvider: React.FC<Props> = ({ children }) => {
  // Initial data
  const initialResetPasswordValues = useMemo(
    () => ({
      password: '',
      confirmPassword: '',
      submit: null,
    }),
    [],
  );

  const resetPassword = useResetPasswordMutation();
  const { token } = useParams();

  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const {
    isOpen: isSuccessPasswordResetDialogOpen,
    openModal: openSuccessPasswordResetDialog,
    closeModal: closeSuccessPasswordResetDialog,
  } = useModal();

  const handleSubmit: FormikConfig<ResetPasswordValues>['onSubmit'] =
    useCallback(
      (values, helpers) => {
        setIsResettingPassword(true);
        resetPassword.mutate(
          { password: values.password, token: token },
          {
            onError: (err) => {
              if (
                err instanceof Yup.ValidationError ||
                err instanceof AxiosError
              ) {
                helpers.setStatus({ success: false });
                helpers.setErrors({
                  submit: 'The link has expired or is invalid.',
                });
                helpers.setSubmitting(false);
              }
            },
            onSuccess() {
              openSuccessPasswordResetDialog();
            },
            onSettled: () => {
              setIsResettingPassword(false);
            },
          },
        );
      },
      [openSuccessPasswordResetDialog, resetPassword, token],
    );

  const providerValues = useMemo(
    () => ({
      resetPasswordValues: initialResetPasswordValues,
      handleSubmit,
      isResettingPassword,
      isSuccessPasswordResetDialogOpen,
      closeSuccessPasswordResetDialog,
    }),
    [
      closeSuccessPasswordResetDialog,
      handleSubmit,
      initialResetPasswordValues,
      isResettingPassword,
      isSuccessPasswordResetDialogOpen,
    ],
  );

  return (
    <ResetPasswordContext.Provider value={providerValues}>
      {children}
    </ResetPasswordContext.Provider>
  );
};

export const useResetPasswordContext = () => useContext(ResetPasswordContext);
